<template>
  <div v-if="editor" class="toolbar">
    <button
      type="button"
      class="toolbar-btn"
      :class="{ 'is-active': editor.isActive('bold') }"
      title="Bold"
      aria-label="Bold"
      data-tooltip="Bold"
      @click="editor.chain().focus().toggleBold().run()"
    >
      <img :src="formatBoldIcon" alt="Bold" class="w-5 h-5" />
    </button>

    <button
      type="button"
      class="toolbar-btn"
      :class="{ 'is-active': editor.isActive('italic') }"
      title="Italic"
      aria-label="Italic"
      data-tooltip="Italic"
      @click="editor.chain().focus().toggleItalic().run()"
    >
      <img :src="formatItalicIcon" alt="Italic" class="w-5 h-5" />
    </button>

    <button
      type="button"
      class="toolbar-btn"
      :class="{ 'is-active': editor.isActive('underline') }"
      title="Underline"
      aria-label="Underline"
      data-tooltip="Underline"
      @click="editor.chain().focus().toggleUnderline().run()"
    >
      <img :src="formatUnderlinedIcon" alt="Underline" class="w-5 h-5" />
    </button>

    <div class="color-picker-container">
      <button
        type="button"
        class="toolbar-btn color-btn"
        title="Text color"
        aria-label="Text color"
        data-tooltip="Text color"
        @click="handleOpenColorPicker"
      >
        <div class="color-icon">
          <span class="text-a">A</span>
          <span class="color-bar" :style="{ backgroundColor: selectedColor }"></span>
        </div>
      </button>

      <input
        ref="colorInputRef"
        type="color"
        class="hidden-color-input"
        :value="selectedColor"
        @input="handleColorInput"
      />
    </div>

    <div class="color-picker-container">
      <button
        type="button"
        class="toolbar-btn color-btn"
        title="Highlight color"
        aria-label="Highlight color"
        data-tooltip="Highlight color"
        @click="handleToggleHighlightPanel"
      >
        <div class="color-icon">
          <img :src="borderColorIcon" alt="Highlight" class="w-5" />
          <span class="color-bar mt-0.5" :style="{ backgroundColor: selectedHighlightDisplay }"></span>
        </div>
      </button>

      <div v-if="showHighlightPanel" class="highlight-panel">
        <button
          type="button"
          class="highlight-option none"
          @click="$emit('highlight-none')"
        >
          None
        </button>

        <div class="highlight-grid">
          <button
            v-for="color in highlightPalette"
            :key="color"
            type="button"
            class="highlight-swatch"
            :style="{ backgroundColor: color }"
            @click="$emit('highlight-select', color)"
          ></button>
        </div>

        <div class="highlight-custom">
          <span>Custom:</span>
          <input
            type="color"
            class="custom-color-input"
            :value="selectedHighlight"
            @input="handleHighlightInput"
          />
        </div>
      </div>
    </div>

    <button
      type="button"
      class="toolbar-btn"
      :class="{ 'is-active': editor.isActive('bulletList') }"
      title="Bulleted list"
      aria-label="Bulleted list"
      data-tooltip="Bulleted list"
      @click="editor.chain().focus().toggleBulletList().run()"
    >
      <img :src="formatListBulletedIcon" alt="Bulleted list" class="w-5 h-5" />
    </button>

    <button
      type="button"
      class="toolbar-btn"
      :class="{ 'is-active': editor.isActive('orderedList') }"
      title="Numbered list"
      aria-label="Numbered list"
      data-tooltip="Numbered list"
      @click="editor.chain().focus().toggleOrderedList().run()"
    >
      <img :src="formatListNumberedIcon" alt="Numbered list" class="w-5 h-5" />
    </button>
  </div>
</template>

<script setup>
import { ref } from "vue";

defineProps({
  editor: {
    type: Object,
    default: null,
  },
  selectedColor: {
    type: String,
    default: "#000000",
  },
  selectedHighlight: {
    type: String,
    default: "#fff8c5",
  },
  selectedHighlightDisplay: {
    type: String,
    default: "transparent",
  },
  showHighlightPanel: {
    type: Boolean,
    default: false,
  },
  highlightPalette: {
    type: Array,
    default: () => [],
  },
  borderColorIcon: {
    type: String,
    default: "",
  },
  formatBoldIcon: {
    type: String,
    default: "",
  },
  formatItalicIcon: {
    type: String,
    default: "",
  },
  formatUnderlinedIcon: {
    type: String,
    default: "",
  },
  formatListBulletedIcon: {
    type: String,
    default: "",
  },
  formatListNumberedIcon: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "open-color-picker",
  "color-input",
  "toggle-highlight-panel",
  "highlight-none",
  "highlight-select",
  "highlight-input",
]);

const colorInputRef = ref(null);

const handleOpenColorPicker = () => {
  emit("open-color-picker");
  colorInputRef.value?.click?.();
};

const handleToggleHighlightPanel = () => {
  emit("toggle-highlight-panel");
};

const handleColorInput = (event) => {
  emit("color-input", event?.target?.value);
};

const handleHighlightInput = (event) => {
  emit("highlight-input", event?.target?.value);
};
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #374957;
}

.toolbar-btn {
  position: relative;
  width: 30px;
  height: 28px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toolbar-btn[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 4px;
  background: #333;
  color: #fff;
  font-size: 12px;
  line-height: 1.1;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, transform 120ms ease;
  z-index: 30;
}

.toolbar-btn[data-tooltip]:hover::after,
.toolbar-btn[data-tooltip]:focus-visible::after {
  opacity: 1;
  transform: translateX(-50%) translateY(-2px);
}

.toolbar-btn:focus-visible {
  outline: 2px solid #1e4670;
  outline-offset: 2px;
}

.toolbar-btn:hover {
  background: rgba(55, 73, 87, 0.12);
}

.toolbar-btn.is-active {
  background: #374957;
}

.toolbar-btn.is-active img {
  filter: brightness(0) invert(1);
}

.color-picker-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.hidden-color-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.color-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.text-a {
  font-weight: 500;
  font-size: 17px;
  line-height: 17px;
  color: #000;
  margin-bottom: 0;
}

.color-bar {
  width: 17px;
  height: 3px;
  border-radius: 1px;
}

.highlight-panel {
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 10px;
  min-width: 210px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.highlight-option.none {
  align-self: flex-start;
  padding: 4px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  font-size: 13px;
  cursor: pointer;
}

.highlight-option.none:hover {
  background: #f0f0f0;
}

.highlight-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
}

.highlight-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.highlight-swatch:hover {
  outline: 2px solid #004879;
}

.highlight-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374957;
}

.custom-color-input {
  width: 34px;
  height: 24px;
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
